const OPUS_BITRATE = 32_000;
const OPUS_FRAME_DURATION_US = 20_000;

function opusConfig(sampleRate: number): AudioEncoderConfig {
	return {
		codec: 'opus',
		sampleRate,
		numberOfChannels: 1,
		bitrate: OPUS_BITRATE,
		opus: {
			format: 'opus',
			frameDuration: OPUS_FRAME_DURATION_US,
			application: 'voip',
			signal: 'voice',
		},
	} as AudioEncoderConfig;
}

export async function isOpusSupported(sampleRate: number): Promise<boolean> {
	if (typeof AudioEncoder === 'undefined' || typeof AudioData === 'undefined') return false;

	try {
		const result = await AudioEncoder.isConfigSupported(opusConfig(sampleRate));
		return !!result.supported;
	} catch {
		return false;
	}
}

export type OpusEncoder = {
	encode: (data: AudioData) => void;
	encodeSamples: (samples: Float32Array<ArrayBuffer>, sampleRate: number) => void;
	close: () => void;
};

/**
 * Encodes mono microphone audio to raw Opus packets. Every output chunk is a
 * single 20 ms packet, which is what `encoding=opus` expects per WebSocket
 * frame. The encoder is configured from the first input, so the sample rate
 * always matches the microphone.
 * @param options Callbacks for encoded packets and encoder failures.
 * @param options.onPacket Receives one raw Opus packet per 20 ms frame.
 * @param options.onError Called when the encoder fails.
 */
export function createOpusEncoder(options: {
	onPacket: (packet: ArrayBuffer) => void;
	onError: (error: Error) => void;
}): OpusEncoder {
	let configuredRate: number | null = null;
	let sampleCursor = 0;

	const encoder = new AudioEncoder({
		output: (chunk) => {
			const packet = new ArrayBuffer(chunk.byteLength);
			chunk.copyTo(packet);
			options.onPacket(packet);
		},
		error: (error) => options.onError(error instanceof Error ? error : new Error(String(error))),
	});

	const ensureConfigured = (sampleRate: number) => {
		if (configuredRate === sampleRate) return true;
		if (encoder.state === 'closed') return false;

		encoder.configure(opusConfig(sampleRate));
		configuredRate = sampleRate;
		sampleCursor = 0;

		return true;
	};

	const toMono = (data: AudioData): AudioData => {
		if (data.numberOfChannels === 1) return data;

		const samples = new Float32Array(data.numberOfFrames);
		data.copyTo(samples, { planeIndex: 0, format: 'f32-planar' });

		const mono = new AudioData({
			format: 'f32-planar',
			sampleRate: data.sampleRate,
			numberOfFrames: data.numberOfFrames,
			numberOfChannels: 1,
			timestamp: data.timestamp,
			data: samples,
		});

		data.close();

		return mono;
	};

	return {
		encode: (data) => {
			const mono = toMono(data);

			try {
				if (ensureConfigured(mono.sampleRate)) encoder.encode(mono);
			} catch (error) {
				options.onError(error instanceof Error ? error : new Error(String(error)));
			} finally {
				mono.close();
			}
		},
		encodeSamples: (samples, sampleRate) => {
			try {
				if (!ensureConfigured(sampleRate)) return;

				const data = new AudioData({
					format: 'f32-planar',
					sampleRate,
					numberOfFrames: samples.length,
					numberOfChannels: 1,
					timestamp: Math.round((sampleCursor / sampleRate) * 1_000_000),
					data: samples,
				});

				sampleCursor += samples.length;
				encoder.encode(data);
				data.close();
			} catch (error) {
				options.onError(error instanceof Error ? error : new Error(String(error)));
			}
		},
		close: () => {
			if (encoder.state !== 'closed') encoder.close();
		},
	};
}
